import { Navigate, useLocation } from "react-router";
import { canPerform } from "@headerhawk/core/roles";
import { isImpersonationTab } from "@/lib/auth/impersonation";
import { useAuthSession, useAuthUser } from "./auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { roleFromProfile } from "@/lib/hooks/use-current-role";
import { useSites } from "@/lib/hooks/use-sites";
import { useReturningUser } from "@/lib/hooks/use-returning-user";
import { ApiError } from "@/lib/api/client";
import { FullScreenLoader } from "@/components/ui/loader";
import { gateLoaderLabel } from "@/lib/demo/session";
import { NoSitesYet } from "./no-sites-yet";

interface RequireAuthProps {
  children: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { isUserLoading } = useAuthUser();
  const { isProvisioned, isProvisioning, provisioningError, profile } =
    useProvisioning();
  const { isLikelyReturningUser } = useReturningUser();
  const {
    data: sitesData,
    isLoading: isSitesLoading,
    error: sitesError,
    refetch: refetchSites,
  } = useSites({
    enabled: isLikelyReturningUser || isProvisioned,
  });
  const location = useLocation();
  // Carry the query string too - a filtered view is worth returning to.
  const from = `${location.pathname}${location.search}`;

  // Hard gate: auth failure (only after loading completes)
  if (!isSessionLoading && !isUserLoading && !isAuthenticated) {
    return <Navigate to="/login" state={{ from }} replace />;
  }

  // Hard gate: 401 from sites or provisioning means session is invalid
  const sitesStatus = (sitesError as ApiError | null)?.status;
  const provisioningStatus = (provisioningError as ApiError | null)?.status;

  if (sitesStatus === 401 || provisioningStatus === 401) {
    return <Navigate to="/login" state={{ from }} replace />;
  }

  // Hard gate: the sites list failed for another reason. Without it, a
  // failed list read as an empty one, and a tenant with sites was offered
  // "Add a site" on every page, or sent to onboarding.
  if (sitesError && !sitesData) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to load your sites</div>
        <div className="text-sm text-muted-foreground">
          {sitesError.message}
        </div>
        <button
          onClick={() => void refetchSites()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // Hard gate: non-401 provisioning error
  if (provisioningError && provisioningStatus !== 401) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to set up your account</div>
        <div className="text-sm text-muted-foreground">
          {provisioningError.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // No sites. A tenant that has had one (the list's `firstSiteCreatedAt`,
  // task 495) has onboarded and since deleted them all: it keeps the
  // dashboard, whose site-scoped pages each offer to add a site, whoever is
  // signed in. That is read from the response that says there are no sites,
  // not from the cached profile, which another tab's change leaves stale.
  //
  // A tenant that has never had one has not finished onboarding: the first
  // run (task 512), one screen that asks for the site's URL. Adding a site is
  // a member's (`POST /sites`), so any member gets it, and the first run
  // names the organization after the site only when the member is its owner.
  // A viewer, who can add nothing, gets a page saying what is missing
  // instead. Choosing needs the role, so this waits for provisioning.
  const withoutSites = () => {
    if (sitesData?.firstSiteCreatedAt) {
      return <>{children}</>;
    }
    if (!isProvisioned) {
      return <FullScreenLoader label={gateLoaderLabel()} />;
    }
    // The first run is nothing but writes, so an operator's read-only view
    // of a member (task 394) gets the page that says what is missing instead.
    if (
      !isImpersonationTab() &&
      canPerform(roleFromProfile(profile), "POST /sites")
    ) {
      return <Navigate to="/onboarding" replace />;
    }
    return <NoSitesYet onSiteAdded={() => void refetchSites()} />;
  };

  // Sites loaded but empty: onboarding incomplete, or every site deleted.
  if (!isSitesLoading && sitesData?.sites.length === 0) {
    return withoutSites();
  }

  // Returning users with sites confirmed: render children immediately,
  // skipping provisioning and remaining loading gates. The /sites query
  // fires early (enabled by isLikelyReturningUser) in parallel with
  // Descope auth refresh and provisioning. Once sites data arrives with
  // at least one site, we know the user is fully set up and can render.
  if (isLikelyReturningUser && sitesData && sitesData.sites.length > 0) {
    return <>{children}</>;
  }

  // First-time users: sequential loading gates (original behavior)
  if (isSessionLoading || isUserLoading) {
    return <FullScreenLoader label={gateLoaderLabel()} />;
  }

  if (isProvisioning) {
    return <FullScreenLoader label={gateLoaderLabel()} />;
  }

  if (!isProvisioned) {
    return <FullScreenLoader label={gateLoaderLabel()} />;
  }

  if (isSitesLoading) {
    return <FullScreenLoader label={gateLoaderLabel()} />;
  }

  const hasSites = sitesData?.sites && sitesData.sites.length > 0;
  if (!hasSites) {
    return withoutSites();
  }

  return <>{children}</>;
}
